import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import Breadcrumb from '../components/Breadcrumb'
import QuoteForm from '../components/QuoteForm'
import TypeFormFlow from '../components/forms/TypeFormFlow'
import { FormConfiguration, FormAnswers } from '../types/formConfig'
import solarFormConfig from '../config/forms/solar_panel_form.json'
import evChargingFormConfig from '../config/forms/ev_charging_form.json'
import airConditioningFormConfig from '../config/forms/air_conditioning_form.json'
import heatPumpFormConfig from '../config/forms/heat_pump_form.json'
import { adjustFormForPostcode } from '../utils/formOrdering'

type ServiceConfig = {
  title: string
  subtitle: string
  description: string
  grantAmount: string | null
  quotationMethod: 'iframe' | 'typeform' | 'form'
  iframeUrl?: string
  formConfig?: FormConfiguration | null
}

type ServiceConfigs = {
  [key: string]: ServiceConfig
}

const Quote = () => {
  const { service } = useParams<{ service: string }>()
  const navigate = useNavigate()
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')

  // Service configuration
  const serviceConfig: ServiceConfigs = {
    'heat-pumps': {
      title: 'Air Source Heat Pumps',
      subtitle: 'Get Your Heat Pump Quote',
      description: 'Complete the form below to receive a personalised quote for your air source heat pump installation.',
      grantAmount: '£7,500',
      quotationMethod: 'typeform',
      iframeUrl: undefined,
      formConfig: heatPumpFormConfig as FormConfiguration
    },
    'solar-panels': {
      title: 'Solar Panel Installation',
      subtitle: 'Get Your Solar Quote',
      description: 'Tell us about your property and energy needs to receive a detailed solar panel installation quote.',
      grantAmount: null,
      quotationMethod: 'typeform',
      iframeUrl: undefined,
      formConfig: solarFormConfig as FormConfiguration
    },
    'ev-charging': {
      title: 'EV Charging Installation',
      subtitle: 'Get Your EV Charger Quote',
      description: 'Get a personalised quote for your home EV charging point installation.',
      grantAmount: null,
      quotationMethod: 'typeform',
      iframeUrl: undefined,
      formConfig: evChargingFormConfig as FormConfiguration
    },
    'air-conditioning': {
      title: 'Air Conditioning Installation',
      subtitle: 'Get Your Air Conditioning Quote',
      description: 'Get a personalised quote for your air conditioning installation with year-round climate control.',
      grantAmount: null,
      quotationMethod: 'typeform',
      iframeUrl: undefined,
      formConfig: airConditioningFormConfig as FormConfiguration
    }
  }

  // Redirect if invalid service
  if (!service || !serviceConfig[service as keyof typeof serviceConfig]) {
    return <Navigate to="/" replace />
  }

  const config = serviceConfig[service as keyof typeof serviceConfig]

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: config.title, path: `/${service}` },
    { label: 'Get a Quote' }
  ]

  // Handle form submission
  const handleFormComplete = async (answers: FormAnswers) => {
    try {
      const apiUrl = import.meta.env.VITE_QUOTE_API_URL

      if (!apiUrl) {
        throw new Error('Quote API URL not configured')
      }

      // Extract turnstileToken from answers and place at root level
      const { turnstileToken, ...formAnswers } = answers as FormAnswers & { turnstileToken?: string }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service,
          formType: 'quote',
          answers: formAnswers,
          turnstileToken,
          timestamp: new Date().toISOString()
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      setSubmitStatus('success')
      setSubmitMessage(config.formConfig?.successMessage || 'Thank you! We\'ll be in touch within 1 working day.')
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitStatus('error')
      setSubmitMessage('Sorry, there was an error submitting your quote request. Please try again or contact us directly.')
      throw error
    }
  }

  // Handle partial form submission (for specific questions)
  const handlePartialSubmit = async (answers: FormAnswers, questionId: string | null) => {
    try {
      const apiUrl = import.meta.env.VITE_QUOTE_API_URL

      if (!apiUrl) {
        console.warn('Quote API URL not configured for partial submit')
        return
      }

      await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service,
          formType: 'partial-quote',
          answers,
          lastQuestionId: questionId,
          timestamp: new Date().toISOString()
        })
      })
    } catch (error) {
      console.error('Partial submit error:', error)
      // Don't throw - we don't want to block the user's progress
    }
  }

  const renderQuotationMethod = () => {
    // Success state
    if (submitStatus === 'success') {
      return (
        <div className="w-full max-w-2xl mx-auto text-center py-12">
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-8 mb-6">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-green-900 mb-3">Quote Request Received!</h3>
            <p className="text-lg text-green-800">{submitMessage}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-primary-green hover:text-primary-green-dark font-medium"
          >
            Return to Homepage
          </button>
        </div>
      )
    }

    // Error state
    if (submitStatus === 'error') {
      return (
        <div className="w-full max-w-2xl mx-auto">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 mb-6">
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-red-900 mb-3 text-center">Something Went Wrong</h3>
            <p className="text-lg text-red-800 text-center mb-6">{submitMessage}</p>
            <div className="text-center">
              <button
                onClick={() => setSubmitStatus('idle')}
                className="bg-primary-green text-white px-6 py-3 rounded-lg hover:bg-primary-green-dark font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )
    }

    // Iframe method (heat pumps)
    if (config.quotationMethod === 'iframe') {
      return (
        <div className="w-full">
          <div className="relative" style={{ minHeight: '800px' }}>
            <iframe
              src={config.iframeUrl!}
              width="100%"
              height="800"
              frameBorder="0"
              title={`${config.title} Quote Form`}
              className="w-full rounded-lg"
              allow="geolocation; microphone; camera; fullscreen; payment; clipboard-read; clipboard-write"
            />
          </div>
        </div>
      )
    }

    if (config.quotationMethod === 'typeform' && config.formConfig) {
      // Apply dynamic question ordering based on postcode availability
      const adjustedConfig = adjustFormForPostcode(config.formConfig)

      return (
        <div className="w-full max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8">
          <TypeFormFlow
            config={adjustedConfig}
            onComplete={handleFormComplete}
            onPartialSubmit={handlePartialSubmit}
          />
        </div>
      )
    }

    // Legacy form method (fallback)
    if (config.quotationMethod === 'form') {
      return (
        <div className="w-full">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-semibold text-dark-plum mb-4">
              Tell us about your requirements
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The more details you provide, the more accurate your quote will be. All information
              is kept confidential and will only be used to provide you with a personalised quote.
            </p>
          </div>

          <QuoteForm
            serviceType={service === 'solar' ? 'solar' : 'ev-charging'}
            onSubmit={(formData) => {
              console.log('Quote form submitted:', formData)
            }}
          />
        </div>
      )
    }

    return null
  }

  return (
    <div className="min-h-[60vh] bg-gray-50">
      {/* Compact Header Section */}
      <section className="bg-gradient-to-r from-dark-background to-dark-plum text-white py-12">
        <div className="container-custom">
          <div className="max-w-3xl">
            <Breadcrumb
              items={breadcrumbItems}
              className="mb-8"
            />
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              {config.subtitle}
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              {config.description}
            </p>

            {config.grantAmount && (
              <div className="inline-flex items-center bg-primary-green bg-opacity-20 backdrop-blur-sm border border-primary-green border-opacity-30 rounded-lg px-6 py-3">
                <svg className="w-5 h-5 text-primary-green mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold text-primary-green">
                  {config.grantAmount} Government Grant Available!
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Quote Form/Iframe Section */}
      <section className="section-padding">
        <div className="container-custom">
          {renderQuotationMethod()}
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-dark-plum mb-2">Free No-Obligation Quote</h3>
              <p className="text-gray-600">Get a detailed quote with no hidden costs or commitments</p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-dark-plum mb-2">24 Hour Response</h3>
              <p className="text-gray-600">We'll get back to you within 24 hours with your personalised quote</p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-dark-plum mb-2">Expert Installation</h3>
              <p className="text-gray-600">Professional installation by certified technicians with full warranty</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Quote